import React, { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Printer, ArrowLeft } from "lucide-react";

// Adarsh Udyog CAD-style printable drawing
// Reproduces the visual language of the reference PDFs:
//  • polygon floor boundary with bold dimension chains per wall segment
//  • magenta/pink rack rows with bay dividers and bay-size labels (e.g. "36-36-18")
//  • title block in bottom-right: project, client, designer, date, sheet #
//  • "ALL DIMENSIONS ARE IN INCHES" note + legend strip

const CELL_PX = 20;
const VIEW = 1000;
const PAD = 140;

const ALPHA = "ABCDEFGHIJKLMNOPQRSTUVWXYZ";
const ptLabel = (i) => (i < 26 ? ALPHA[i] : ALPHA[Math.floor(i / 26) - 1] + ALPHA[i % 26]);
const fmt = (v, d = 1) => Number(v || 0).toFixed(d);

export default function FloorPlanPrint() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [layout, setLayout] = useState(null);
  const [library, setLibrary] = useState([]);
  const [quote, setQuote] = useState(null);   // latest quotation for this layout, if any
  const [genBusy, setGenBusy] = useState(false);

  useEffect(() => {
    api.get(`/floorplan/layouts/${id}`).then((r) => setLayout(r.data));
    api.get(`/floorplan/library`).then((r) => setLibrary(r.data));
    api.get(`/floorplan/quotations`).then((r) => {
      const match = (r.data || []).find((q) => q.layout_id === id);
      if (match) setQuote(match);
    });
  }, [id]);

  const gridSize = layout?.grid_size || 12;
  const points = layout?.points || [];
  const placed = layout?.placed_items || [];

  const sides = useMemo(() => {
    if (points.length < 3) return [];
    const out = [];
    for (let i = 0; i < points.length; i++) {
      const a = points[i], b = points[(i + 1) % points.length];
      const dx = b.x - a.x, dy = b.y - a.y;
      const lenPx = Math.hypot(dx, dy);
      const len = (lenPx / CELL_PX) * gridSize;   // inches
      out.push({
        index: i, a, b, dx, dy, lenPx, len,
        midX: (a.x + b.x) / 2, midY: (a.y + b.y) / 2,
      });
    }
    return out;
  }, [points, gridSize]);

  const stats = useMemo(() => {
    if (points.length < 3) return { area: 0, perimeter: 0 };
    let s = 0;
    for (let i = 0; i < points.length; i++) {
      const a = points[i], b = points[(i + 1) % points.length];
      s += a.x * b.y - b.x * a.y;
    }
    const perim = sides.reduce((v, sd) => v + sd.len, 0);
    const areaIn2 = (Math.abs(s) / 2) * (gridSize / CELL_PX) ** 2;
    return { area: areaIn2 / 144, perimeter: perim };   // area in ft²
  }, [points, sides, gridSize]);

  const bom = useMemo(() => {
    // aggregate bays by (library name + bay size) — same logic as backend
    const g = {};
    placed.forEach((p) => {
      const lib = library.find((l) => l.id === p.library_item_id);
      if (!lib) return;
      const bays = p.bays && p.bays.length ? p.bays : [lib.default_length_in || (lib.default_length_ft * 12)];
      const uts = p.unit_types || (bays.map((_, i) => (i === 0 ? "main" : "addon")));
      bays.forEach((bw, i) => {
        const ut = uts[i] || (i === 0 ? "main" : "addon");
        const key = `${lib.kind}|${ut}|${bw}`;
        if (!g[key]) g[key] = { name: lib.name.replace(/ Main| Add-on/g, ""), kind: lib.kind, ut, bw, qty: 0 };
        g[key].qty += 1;
      });
    });
    return Object.values(g).sort((a, b) => a.kind.localeCompare(b.kind) || a.ut.localeCompare(b.ut) || a.bw - b.bw);
  }, [placed, library]);

  // --- helpers ---
  const inwardNormal = (sd) => {
    const len = Math.hypot(sd.dx, sd.dy) || 1;
    let nx = -sd.dy / len, ny = sd.dx / len;
    // choose so it points away from polygon centroid
    const cx = points.reduce((s, p) => s + p.x, 0) / points.length;
    const cy = points.reduce((s, p) => s + p.y, 0) / points.length;
    const tx = sd.midX + nx * 2, ty = sd.midY + ny * 2;
    const dist1 = Math.hypot(tx - cx, ty - cy);
    const dist2 = Math.hypot(sd.midX - nx * 2 - cx, sd.midY - ny * 2 - cy);
    if (dist1 < dist2) { nx = -nx; ny = -ny; }
    return { nx, ny };
  };

  const generateQuote = async () => {
    if (placed.length === 0) { alert("Add racks first"); return; }
    setGenBusy(true);
    try {
      const { data } = await api.post("/floorplan/quotations", { layout_id: id });
      setQuote(data);
    } finally { setGenBusy(false); }
  };

  if (!layout) {
    return <div className="p-8 text-sm text-ink-muted">Loading…</div>;
  }

  // normalise polygon into viewbox
  const minX = Math.min(...points.map((p) => p.x)), maxX = Math.max(...points.map((p) => p.x));
  const minY = Math.min(...points.map((p) => p.y)), maxY = Math.max(...points.map((p) => p.y));
  const boxW = maxX - minX, boxH = maxY - minY;

  return (
    <div className="min-h-screen bg-white text-black">
      {/* Non-print toolbar */}
      <div className="no-print sticky top-0 bg-white border-b border-line flex items-center justify-between px-6 py-3 z-10">
        <button onClick={() => navigate(`/floorplan/${id}`)} className="btn-ghost" data-testid="fp-print-back">
          <ArrowLeft size={14} /> Back to editor
        </button>
        <div className="flex items-center gap-2">
          {!quote && (
            <button onClick={generateQuote} disabled={genBusy} className="btn-secondary" data-testid="fp-print-gen-quote">
              {genBusy ? "Generating…" : "Generate quotation"}
            </button>
          )}
          <button onClick={() => window.print()} className="btn-primary" data-testid="fp-print-btn">
            <Printer size={14} /> Print / Save as PDF
          </button>
        </div>
      </div>

      {/* Print surface — A3 landscape ~ 1600×1130 px */}
      <div className="cad-sheet mx-auto bg-white relative" style={{
        width: "297mm", minHeight: "210mm", padding: "14mm 16mm",
        fontFamily: "'IBM Plex Sans', sans-serif", color: "#0a0a0a",
      }}>
        {/* Sheet border */}
        <div className="absolute inset-0 m-[6mm] border-2 border-black" style={{ pointerEvents: "none" }} />
        <div className="absolute" style={{ left: "10mm", top: "10mm", right: "10mm", bottom: "10mm", border: "1px solid #000", pointerEvents: "none" }} />

        {/* Top strip: company header */}
        <div className="flex items-center justify-between border-b-2 border-black pb-2 mb-3 relative">
          <div className="flex items-center gap-3">
            <div className="flex flex-col items-center">
              <div className="text-red-700 font-black tracking-wide" style={{ fontFamily: "'Archivo', sans-serif", fontSize: 22, lineHeight: 1 }}>AJ</div>
              <div className="text-green-700 font-black tracking-wide" style={{ fontFamily: "'Archivo', sans-serif", fontSize: 12, lineHeight: 1 }}>Adarsh Udyog</div>
              <div className="text-[8px] tracking-widest uppercase text-black">A Complete Storage Solution</div>
            </div>
          </div>
          <div className="text-center flex-1 px-4">
            <div className="font-black uppercase tracking-widest" style={{ fontFamily: "'Archivo', sans-serif", fontSize: 18 }}>
              {layout.name || "Retail Shop Layout"}
            </div>
            <div className="text-[10px] uppercase tracking-wider mt-0.5">
              {layout.client?.company || layout.client?.name || "—"} · {layout.client?.address || ""}
            </div>
          </div>
          <div className="text-right text-[10px]">
            <div className="mono">Plan # <b>{layout.plan_number}</b></div>
            <div className="mono">Date: {new Date(layout.updated_at || layout.created_at).toLocaleDateString("en-IN")}</div>
            <div className="mono">Designer: {layout.created_by_name}</div>
          </div>
        </div>

        <div className="grid grid-cols-[1fr_280px] gap-3 items-start">
          {/* DRAWING AREA */}
          <div className="relative border border-black" style={{ background: "#fff" }}>
            <svg viewBox={`${minX - PAD} ${minY - PAD} ${boxW + PAD * 2} ${boxH + PAD * 2}`}
                 style={{ width: "100%", height: "auto", display: "block" }}>
              {/* polygon floor boundary */}
              <polygon points={points.map((p) => `${p.x},${p.y}`).join(" ")}
                       fill="#ffffff" stroke="#000000" strokeWidth="2.5" />

              {/* Corner labels A, B, C, ... */}
              {points.map((p, i) => (
                <g key={`p${i}`}>
                  <circle cx={p.x} cy={p.y} r={3} fill="#000" />
                  <text x={p.x + 8} y={p.y - 6} fontFamily="monospace" fontWeight="700"
                        fontSize={11} fill="#000">{ptLabel(i)}</text>
                </g>
              ))}

              {/* Dimension chains along each wall (outside) */}
              {sides.map((sd) => {
                const ang = Math.atan2(sd.dy, sd.dx);
                const { nx, ny } = inwardNormal(sd);
                const outX = -nx, outY = -ny;
                const off = 34;  // dim line offset from wall
                const x1 = sd.a.x + outX * off, y1 = sd.a.y + outY * off;
                const x2 = sd.b.x + outX * off, y2 = sd.b.y + outY * off;
                const label = `${fmt(sd.len, 0)}″`;
                return (
                  <g key={`dim${sd.index}`}>
                    {/* extension lines */}
                    <line x1={sd.a.x} y1={sd.a.y} x2={x1} y2={y1} stroke="#000" strokeWidth="0.8" />
                    <line x1={sd.b.x} y1={sd.b.y} x2={x2} y2={y2} stroke="#000" strokeWidth="0.8" />
                    {/* dim line */}
                    <line x1={x1} y1={y1} x2={x2} y2={y2} stroke="#000" strokeWidth="1.1"
                          markerStart="url(#dimArrow)" markerEnd="url(#dimArrow)" />
                    <text x={(x1 + x2) / 2} y={(y1 + y2) / 2 - 4}
                          textAnchor="middle" fill="#000"
                          fontFamily="monospace" fontWeight="700" fontSize={12}
                          transform={`rotate(${ang * 180 / Math.PI} ${(x1 + x2) / 2} ${(y1 + y2) / 2})`}>
                      {label}
                    </text>
                  </g>
                );
              })}
              <defs>
                <marker id="dimArrow" viewBox="0 0 10 10" refX="5" refY="5" markerWidth="6" markerHeight="6" orient="auto-start-reverse">
                  <path d="M 0 0 L 10 5 L 0 10 z" fill="#000" />
                </marker>
              </defs>

              {/* Rack rows with bay dividers (magenta) + row-type labels */}
              {placed.map((it) => {
                const lib = library.find((l) => l.id === it.library_item_id);
                const fill = lib?.color || "#e91e63";
                const lenIn = it.length_in ?? (it.length_ft * 12);
                const depIn = it.depth_in  ?? (it.depth_ft * 12);
                const lenPx = (lenIn / gridSize) * CELL_PX;
                const depPx = (depIn / gridSize) * CELL_PX;
                const cx = it.x + lenPx / 2, cy = it.y + depPx / 2;
                const bays = it.bays || [lenIn];
                const pxPerInch = lenPx / Math.max(lenIn, 1);
                let cursor_in = 1.0;
                const pieces = bays.map((bw, k) => {
                  const x0 = cursor_in * pxPerInch;
                  const w  = bw * pxPerInch;
                  cursor_in += bw + 0.5;
                  return { x0, w, bw, k };
                });
                // row-type label
                const rowLabel = (
                  it.kind === "wall_rack" ? "WALL RACK" :
                  it.kind === "gondola" ? "CENTRE GONDOLA" :
                  it.kind === "corner_rack" ? "CORNER" :
                  it.kind === "billing_counter" ? "BILLING COUNTER" :
                  it.kind === "end_cap" ? "END CAP" :
                  (it.name || "RACK").toUpperCase()
                );
                return (
                  <g key={it.id} transform={`rotate(${it.rotation} ${cx} ${cy})`}>
                    <rect x={it.x} y={it.y} width={lenPx} height={depPx}
                          fill={fill} fillOpacity={0.92} stroke="#000" strokeWidth="0.8" />
                    {pieces.length > 1 && pieces.slice(0, -1).map((b, k) => (
                      <line key={`d${k}`}
                            x1={it.x + b.x0 + b.w} y1={it.y}
                            x2={it.x + b.x0 + b.w} y2={it.y + depPx}
                            stroke="#fff" strokeWidth="0.8" strokeOpacity="0.8" />
                    ))}
                    {pieces.map((b, k) => (
                      b.w >= 10 && (
                        <text key={`bl${k}`}
                              x={it.x + b.x0 + b.w / 2}
                              y={it.y + depPx / 2 + 3}
                              textAnchor="middle" fill="#fff"
                              fontFamily="monospace" fontWeight="700"
                              fontSize={Math.min(11, Math.max(7, b.w / 4))}>
                          {b.bw % 1 === 0 ? b.bw : b.bw.toFixed(1)}
                        </text>
                      )
                    ))}
                    {/* Row-type label outside the row (long rows only, no labels for tiny corner arms) */}
                    {lenPx >= 50 && it.kind !== "corner_rack" && (
                      <text x={cx} y={it.y - 4}
                            textAnchor="middle" fill="#0f172a"
                            fontFamily="'Archivo', sans-serif" fontWeight="800"
                            fontSize={9} letterSpacing="1.5">
                        {rowLabel}
                      </text>
                    )}
                  </g>
                );
              })}

              {/* Customer-flow arrow: entrance → billing counter (if both exist) */}
              {(() => {
                const entrance = (layout.markers || []).find((m) => m.kind === "entrance");
                const billing = placed.find((p) => p.kind === "billing_counter");
                if (!entrance || !billing) return null;
                const bx = billing.x + ((billing.length_in ?? billing.length_ft * 12) / gridSize * CELL_PX) / 2;
                const by = billing.y + ((billing.depth_in ?? billing.depth_ft * 12) / gridSize * CELL_PX) / 2;
                return (
                  <g>
                    <line x1={entrance.x} y1={entrance.y} x2={bx} y2={by}
                          stroke="#16a34a" strokeWidth="1.5" strokeDasharray="6 3"
                          markerEnd="url(#flowArrow)" />
                    <text x={(entrance.x + bx) / 2 + 12} y={(entrance.y + by) / 2 - 4}
                          fill="#15803d" fontFamily="'Archivo', sans-serif"
                          fontWeight="800" fontSize="9" letterSpacing="1.5">
                      CUSTOMER FLOW
                    </text>
                  </g>
                );
              })()}
              <defs>
                <marker id="flowArrow" viewBox="0 0 10 10" refX="9" refY="5"
                        markerWidth="7" markerHeight="7" orient="auto-start-reverse">
                  <path d="M 0 0 L 10 5 L 0 10 z" fill="#16a34a" />
                </marker>
              </defs>

              {/* Entrance / exit markers */}
              {(layout.markers || []).filter((m) => ["entrance", "shutter"].includes(m.kind)).map((m) => (
                <g key={m.id}>
                  <rect x={m.x - 12} y={m.y - 12} width={24} height={24}
                        fill="none" stroke="#16a34a" strokeWidth="2" strokeDasharray="3 2" />
                  <text x={m.x} y={m.y + 4} textAnchor="middle" fill="#16a34a"
                        fontFamily="monospace" fontWeight="700" fontSize={10}>
                    {m.kind === "entrance" ? "ENTRY" : "SHUTTER"}
                  </text>
                </g>
              ))}
            </svg>
          </div>

          {/* Side panel: BOM + title block */}
          <div className="text-[9.5px] space-y-3">
            <div className="border border-black">
              <div className="bg-black text-white px-2 py-1 font-bold uppercase tracking-widest text-[10px]">Bill of Materials</div>
              <table className="w-full text-[8.5px]">
                <thead>
                  <tr className="bg-gray-100">
                    <th className="border border-black px-1.5 py-1 text-left font-bold">Item</th>
                    <th className="border border-black px-1.5 py-1 text-left font-bold">Type</th>
                    <th className="border border-black px-1.5 py-1 text-right font-bold">Bay"</th>
                    <th className="border border-black px-1.5 py-1 text-right font-bold">Qty</th>
                  </tr>
                </thead>
                <tbody>
                  {bom.length === 0 && (
                    <tr><td colSpan={4} className="border border-black px-1.5 py-3 text-center text-gray-500">No racks placed yet</td></tr>
                  )}
                  {bom.map((r, i) => (
                    <tr key={i}>
                      <td className="border border-black px-1.5 py-0.5 font-bold">{r.name}</td>
                      <td className="border border-black px-1.5 py-0.5 uppercase mono">{r.ut}</td>
                      <td className="border border-black px-1.5 py-0.5 mono text-right">{r.bw}</td>
                      <td className="border border-black px-1.5 py-0.5 mono text-right">{r.qty}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="border border-black p-2 text-[8.5px]">
              <div className="font-bold uppercase tracking-widest mb-1">Legend</div>
              <Legend c="#e91e63" l="Wall Rack · magenta (18″ depth)" />
              <Legend c="#9c27b0" l="Centre Gondola · purple (36″ 2-side)" />
              <Legend c="#ff6f00" l="Corner Rack · orange (24×24)" />
              <Legend c="#475569" l="Counter / Billing" />
              <Legend c="#16a34a" l="Entrance / Shutter" />
            </div>

            <div className="border border-black p-2 text-[8.5px] leading-tight">
              <div className="font-bold uppercase tracking-widest mb-1">Sheet Notes</div>
              <div>ALL DIMENSIONS ARE IN INCHES, UNLESS OTHERWISE SPECIFIED.</div>
              <div className="mt-1">Bay rule: Main +1″ leg allowance; Add-on 0.5″ shared-leg gap.</div>
              <div className="mt-1">Aisle preferred 48″ · minimum 36″.</div>
            </div>

            <SideViewPanel />

            {quote && (
              <div className="border border-black p-2 text-[9px]">
                <div className="font-bold uppercase tracking-widest mb-1">Quotation</div>
                <div className="flex justify-between"><span>Quote #</span><span className="mono">{quote.quote_number}</span></div>
                <div className="flex justify-between"><span>Subtotal</span><span className="mono">₹{quote.subtotal.toLocaleString("en-IN")}</span></div>
                <div className="flex justify-between"><span>GST @ {quote.gst_percent}%</span><span className="mono">₹{quote.gst_amount.toLocaleString("en-IN")}</span></div>
                <div className="flex justify-between font-bold border-t border-black mt-1 pt-1"><span>Grand total</span><span className="mono">₹{quote.grand_total.toLocaleString("en-IN")}</span></div>
              </div>
            )}

            <div className="border-2 border-black p-2 text-[8.5px]">
              <div className="font-bold uppercase tracking-widest mb-1">Title Block</div>
              <Row k="Project" v={layout.name} />
              <Row k="Client" v={layout.client?.name || "—"} />
              <Row k="Company" v={layout.client?.company || "—"} />
              <Row k="Contact" v={layout.client?.contact || "—"} />
              <Row k="Address" v={layout.client?.address || "—"} />
              <Row k="Area" v={`${fmt(stats.area, 1)} ft² · Perim ${fmt(stats.perimeter / 12, 1)} ft`} />
              <Row k="Drawn by" v={layout.created_by_name} />
              <Row k="Drawing #" v={layout.plan_number} />
            </div>
          </div>
        </div>
      </div>

      <style>{`
        @media print {
          .no-print { display: none !important; }
          body { margin: 0; }
          .cad-sheet { box-shadow: none !important; width: 100% !important; min-height: auto !important; }
          @page { size: A3 landscape; margin: 8mm; }
        }
      `}</style>
    </div>
  );
}

function Row({ k, v }) {
  return (
    <div className="grid grid-cols-[72px_1fr] gap-1 border-t border-gray-400 py-0.5 first:border-t-0">
      <div className="uppercase text-[8px] text-gray-700 tracking-widest">{k}</div>
      <div className="mono text-[9px] font-bold truncate">{v || "—"}</div>
    </div>
  );
}

function Legend({ c, l }) {
  return (
    <div className="flex items-center gap-1.5 py-0.5">
      <span style={{ width: 10, height: 10, background: c, display: "inline-block", border: "1px solid #000" }} />
      <span>{l}</span>
    </div>
  );
}

function SideViewPanel() {
  // Tiny CAD-style elevations (front / side views) for Wall Rack, Centre Gondola, End Cap.
  // Each profile is drawn at ~1:24 inches/px so they fit the side panel.
  return (
    <div className="border border-black p-2 text-[8.5px]">
      <div className="font-bold uppercase tracking-widest mb-1.5">Side Views (Reference)</div>
      <div className="space-y-2">
        <SideViewItem title="Wall Rack — 36×18×84″" depth={18} height={84}>
          <svg viewBox="0 0 240 110" className="w-full" style={{ display: "block" }}>
            {/* shelves stacked */}
            {[0, 1, 2, 3, 4].map((i) => (
              <line key={i} x1="20" y1={20 + i * 16} x2="220" y2={20 + i * 16}
                    stroke="#0f172a" strokeWidth="1" />
            ))}
            {/* uprights */}
            <line x1="20" y1="18" x2="20" y2="100" stroke="#0f172a" strokeWidth="2" />
            <line x1="220" y1="18" x2="220" y2="100" stroke="#0f172a" strokeWidth="2" />
            {/* base */}
            <rect x="18" y="100" width="204" height="3" fill="#e91e63" />
            {/* labels */}
            <text x="120" y="14" textAnchor="middle" fontFamily="monospace" fontSize="8" fontWeight="700">36″</text>
            <text x="232" y="60" fontFamily="monospace" fontSize="8" fontWeight="700">84″</text>
          </svg>
        </SideViewItem>

        <SideViewItem title="Centre Gondola — 36×36×72″ (2-side)">
          <svg viewBox="0 0 240 110" className="w-full" style={{ display: "block" }}>
            {/* both sides shelves */}
            {[0, 1, 2, 3].map((i) => (
              <g key={i}>
                <line x1="20" y1={25 + i * 18} x2="115" y2={25 + i * 18} stroke="#0f172a" strokeWidth="1" />
                <line x1="125" y1={25 + i * 18} x2="220" y2={25 + i * 18} stroke="#0f172a" strokeWidth="1" />
              </g>
            ))}
            {/* uprights */}
            <line x1="20" y1="22" x2="20" y2="100" stroke="#0f172a" strokeWidth="2" />
            <line x1="120" y1="22" x2="120" y2="100" stroke="#0f172a" strokeWidth="2" />
            <line x1="220" y1="22" x2="220" y2="100" stroke="#0f172a" strokeWidth="2" />
            {/* base */}
            <rect x="18" y="100" width="204" height="3" fill="#9c27b0" />
            <text x="120" y="18" textAnchor="middle" fontFamily="monospace" fontSize="8" fontWeight="700">36″ + 36″</text>
            <text x="232" y="60" fontFamily="monospace" fontSize="8" fontWeight="700">72″</text>
          </svg>
        </SideViewItem>

        <SideViewItem title="End Cap — 24×18×84″">
          <svg viewBox="0 0 160 110" className="w-full" style={{ display: "block" }}>
            {[0, 1, 2, 3, 4].map((i) => (
              <line key={i} x1="20" y1={20 + i * 16} x2="140" y2={20 + i * 16}
                    stroke="#0f172a" strokeWidth="1" />
            ))}
            <line x1="20" y1="18" x2="20" y2="100" stroke="#0f172a" strokeWidth="2" />
            <line x1="140" y1="18" x2="140" y2="100" stroke="#0f172a" strokeWidth="2" />
            <rect x="18" y="100" width="124" height="3" fill="#16a34a" />
            <text x="80" y="14" textAnchor="middle" fontFamily="monospace" fontSize="8" fontWeight="700">24″</text>
          </svg>
        </SideViewItem>
      </div>
    </div>
  );
}

function SideViewItem({ title, children }) {
  return (
    <div>
      <div className="text-[7.5px] uppercase tracking-widest text-gray-700 mb-0.5">{title}</div>
      <div className="border border-gray-400 bg-white">{children}</div>
    </div>
  );
}
